import { useEffect, useState } from 'react';
import { api } from './api';
import { useAuthStore, PlatformUser } from './auth.store';
import { AppType } from './platform';
import { migrateAuthStorage } from './auth-migration';
import { getPlatformToken } from './platform-session';

let inFlightUserSync: Promise<PlatformUser | null> | null = null;
let inFlightUserSyncToken: string | null = null;

export function getAllowedApps(user: PlatformUser | null | undefined): AppType[] {
  if (!user?.allowedApps || user.allowedApps.length === 0) {
    return ['trade_bridge'];
  }
  return user.allowedApps;
}

export function hasAppAccess(user: PlatformUser | null | undefined, appType: AppType): boolean {
  return getAllowedApps(user).includes(appType);
}

export function getAppSubscription(user: PlatformUser | null | undefined, appType: AppType) {
  return user?.appSubscriptions?.find(item => item.appType === appType) || null;
}

export function hasActiveAppSubscription(user: PlatformUser | null | undefined, appType: AppType): boolean {
  if (user?.role === 'admin') return true;
  const subscription = getAppSubscription(user, appType);
  return Boolean(subscription?.isActive);
}

function syncPlatformUser(token: string): Promise<PlatformUser | null> {
  if (inFlightUserSync && inFlightUserSyncToken === token) {
    return inFlightUserSync;
  }

  inFlightUserSyncToken = token;
  inFlightUserSync = api.get('/users/me')
    .then(response => response.data as PlatformUser)
    .catch(() => null)
    .finally(() => {
      inFlightUserSync = null;
      inFlightUserSyncToken = null;
    });

  return inFlightUserSync;
}

export function usePlatformAuth() {
  const token = useAuthStore(s => s.token);
  const user = useAuthStore(s => s.user);
  const setUser = useAuthStore(s => s.setUser);
  const logout = useAuthStore(s => s.logout);
  const [hydrated, setHydrated] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [checkedSession, setCheckedSession] = useState(false);
  const [resolvedToken, setResolvedToken] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
    if (useAuthStore.persist.hasHydrated()) setHydrated(true);
    return unsub;
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    migrateAuthStorage();
    setResolvedToken(getPlatformToken());
  }, [hydrated, token]);

  useEffect(() => {
    if (!hydrated) return;
    if (resolvedToken === undefined) {
      return;
    }
    if (!resolvedToken) {
      setSyncing(false);
      setCheckedSession(true);
      return;
    }

    let cancelled = false;
    const cachedUser = useAuthStore.getState().user;
    const hasCachedUser = Boolean(cachedUser);
    setSyncing(!hasCachedUser);
    setCheckedSession(hasCachedUser);

    syncPlatformUser(resolvedToken)
      .then(nextUser => {
        if (cancelled) return;
        if (!cancelled) {
          if (nextUser) {
            setUser(nextUser);
          } else if (!hasCachedUser) {
            logout();
            setResolvedToken(null);
          }
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setSyncing(false);
          setCheckedSession(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [hydrated, logout, resolvedToken, setUser]);

  return {
    hydrated,
    token: resolvedToken ?? null,
    user,
    syncing,
    ready: hydrated && resolvedToken !== undefined && (!resolvedToken || Boolean(user) || checkedSession),
  };
}
